import { Box, InputBase, useTheme } from "@mui/material"
import { useEffect, useMemo, useState } from "react";

import ArrowRightAltIcon from '@mui/icons-material/ArrowRightAlt';
import { tokens } from "../../../theme";
import SHModal from "../../theme/Modal/SHModal";
import Shbutton from "../../theme/buttons/Shbutton";
import Subheading from "../../theme/text/Subheading";
import Text from "../../theme/text/Text";
import ButtonGroup from "../../theme/buttons/ButtonGroup";
import { useTranslation } from "react-i18next";
import { useAddWarehouseLocationMutation } from "../../../newapi/warehouse/warehouselocationSlice";
import { showToastMessagePostRequest, showToastMessageRequestError } from "../../../utils/toasts";
import * as yup from "yup";
import ShValidatedInput from "../../theme/inputs/ShValidatedInput";
import ShSwitch from "../../theme/inputs/ShSwitch";
import ShValidatedInputDropdown from "../../theme/dropdowns/ShValidatedInputDropdown";
import { yupRequired, yupNumberRequired } from "../../../utils/validation";
import { useGetWarehouseLocationTypesSelectQuery } from "../../../newapi/warehouse/warehouseLocationTypeSlice";
import { useGetReachTypesSelectQuery } from "../../../newapi/warehouse/reachTypeSlice";
import { useGetWarehouseZonesSelectQuery, useGetWarehousesSelectQuery } from "../../../newapi/warehouse/warehouseApiSlice";
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import cx from "classnames"
import { useSelector } from "react-redux";
import EMGeneralExplainerBox from "../ModalsPopover/explainers/EMGeneralExplainerBox";
import { packstationZoneRangeOptions } from "../../../utils/staticEnums";
import MConfirmation from "./MConfirmation";

// An alphabetic range counts like spreadsheet columns (A - Z, AA - AZ, BA ... ZZZ), mirroring the backend
const ALPHABETIC_RANGE_PATTERN = /^[A-Z]{1,3}$/;
const lettersToNumber = (letters) => {
    let number = 0;
    for (const letter of letters) {
        number = number * 26 + (letter.charCodeAt(0) - 64);
    }
    return number;
};

const MAddWarehouseLocation = (props) => {
    const {t} = useTranslation();
    const theme = useTheme();
    const colors = tokens(theme.palette.mode);

    const [selected, setSelected] = useState(-1);
    const [returnLocationConfirmOpen, setReturnLocationConfirmOpen] = useState(false);
    const [addWarehouseLocation,{isLoading}] = useAddWarehouseLocationMutation();

    const select = (selected) => {
        setSelected(selected);
    }
    
    const { data: locationTypes, locationTypesLoading } = useGetWarehouseLocationTypesSelectQuery();
    const { data: reachTypes, reachTypesLoading } = useGetReachTypesSelectQuery();
    const { data: warehouses, warehousesLoading } = useGetWarehousesSelectQuery({includeBulk: true});
    const selectedActiveWarehouseId = useSelector(state => state.warehouse.activeId);

    const { data: warehouseZones, warehouseZonesLoading } = useGetWarehouseZonesSelectQuery();

    const [locationInfo, setLocationInfo] = useState({
        location: "",
        useRange: false,
        rangeType: "numeric",
        rangeStart: "",
        rangeEnd: "",
        reachTypeId: null,
        warehouseLocationTypeId: null,
        warehouseId: null,
        warehouseZoneId: null,
        returnLocation: false,
        defaultLocation: false,
        packstationZoneRange: null
    });

    useEffect(() => {
        if (props.open) {

            let warehouseId = null;
            if (warehouses?.data.length === 1) {
                warehouseId = warehouses.data[0].id;
            } else {
                if(selectedActiveWarehouseId !== -1){
                    warehouseId = selectedActiveWarehouseId;
                }

            }
            setLocationInfo({
                location: "",
                useRange: false,
                rangeType: "numeric",
                rangeStart: "",
                rangeEnd: "",
                reachTypeId: null,
                warehouseLocationTypeId: null,
                warehouseId: warehouseId,
                warehouseZoneId: null,
                returnLocation: false,
                defaultLocation: false,
                packstationZoneRange: null
            });
        }
    }, [props.open]);
    const [fieldErrors, setFieldErrors] = useState({});

    const rules = {
        location: yupRequired(locationInfo.useRange ? 'prefix' : 'location', t),
        reachTypeId: yupNumberRequired('reachtype', t),
        warehouseLocationTypeId: yupNumberRequired('location_type', t),
        warehouseId: yupNumberRequired('warehouse', t),
        warehouseZoneId: yupNumberRequired('warehouse_zone', t),
        ...(locationInfo.useRange && locationInfo.rangeType === "alphabetic" ? {
            rangeStart: yupRequired('from', t).matches(ALPHABETIC_RANGE_PATTERN, t("range_letters_only")),
            rangeEnd: yupRequired('to', t).matches(ALPHABETIC_RANGE_PATTERN, t("range_letters_only"))
                .test('not-before-start', t("range_end_before_start"), (value, context) =>
                    !ALPHABETIC_RANGE_PATTERN.test(context.parent.rangeStart ?? "") || lettersToNumber(value) >= lettersToNumber(context.parent.rangeStart)
                ),
        } : {}),
        ...(locationInfo.useRange && locationInfo.rangeType !== "alphabetic" ? {
            rangeStart: yupNumberRequired('from', t, false).integer(t("range_whole_numbers")).min(0, t("range_whole_numbers")),
            rangeEnd: yupNumberRequired('to', t, false).integer(t("range_whole_numbers")).min(yup.ref('rangeStart'), t("range_end_before_start")),
        } : {}),
    };
    
    const schema = yup.object().shape(rules);

    // Preview of what the range creates, so the user sees the padding and the amount before saving
    const rangePreview = useMemo(() => {
        if (!locationInfo.useRange || !locationInfo.location) return null;

        if (locationInfo.rangeType === "alphabetic") {
            if (!ALPHABETIC_RANGE_PATTERN.test(locationInfo.rangeStart) || !ALPHABETIC_RANGE_PATTERN.test(locationInfo.rangeEnd)) return null;

            const start = lettersToNumber(locationInfo.rangeStart);
            const end = lettersToNumber(locationInfo.rangeEnd);
            if (end < start) return null;

            return {
                first: `${locationInfo.location}${locationInfo.rangeStart}`,
                last: `${locationInfo.location}${locationInfo.rangeEnd}`,
                amount: (end - start) + 1
            };
        }

        const start = parseInt(locationInfo.rangeStart, 10);
        const end = parseInt(locationInfo.rangeEnd, 10);
        if (isNaN(start) || isNaN(end) || start < 0 || end < start) return null;

        const padLength = `${end}`.length;
        const format = (number) => `${locationInfo.location}${`${number}`.padStart(padLength, "0")}`;

        return {
            first: format(start),
            last: format(end),
            amount: (end - start) + 1
        };
    }, [locationInfo.useRange, locationInfo.rangeType, locationInfo.location, locationInfo.rangeStart, locationInfo.rangeEnd]);
   
    const SaveWarehouseLocation = async (locationInfo, props) => {
        try {
            if (isLoading) return;
            setFieldErrors({});
            await schema.validate(locationInfo, { abortEarly: false });
            const data = await addWarehouseLocation(locationInfo).unwrap()
            showToastMessagePostRequest(t, data);

            // reset object
            setLocationInfo({
                location: "",
                useRange: false,
                rangeType: "numeric",
                rangeStart: "",
                rangeEnd: "",
                reachTypeId: null,
                warehouseLocationTypeId: null,
                warehouseId: null,
                warehouseZoneId: null,
                returnLocation: false,
                defaultLocation: false,
                packstationZoneRange: null
            });

            props.handleClose();
        } catch (err) {
            if (err.name === 'ValidationError') {
                // Create an errors object where keys are field names and values are error messages
                const errors = err.inner.reduce((acc, currentError) => ({
                  ...acc,
                  [currentError.path]: currentError.message
                }), {});
          
                // Set the validation errors in the state
                setFieldErrors(errors);
              } else {
                // Handle other errors, such as network errors
                showToastMessageRequestError(t("create_failed"), t, err);
              }
        }
    };
    let isStandAlone = false;
    if (window.matchMedia('(display-mode: standalone)').matches) {
        isStandAlone = true;
    }

    const handleChange = (key, value) => {
        setLocationInfo(prev => ({
          ...prev,
          [key]: value
        }));
        setFieldErrors({
            ...fieldErrors,
            [key]: ''
          });
    };

    // Letters are stored upper case so the preview matches what the backend creates
    const handleRangeBoundChange = (key, value) => {
        handleChange(key, locationInfo.rangeType === "alphabetic" ? value.toUpperCase() : value);
    };

    const handleRangeTypeChange = (value) => {
        if (!value) return;
        // Numbers and letters are not interchangeable, so the bounds start over when the type switches
        setLocationInfo(prev => ({
          ...prev,
          rangeType: value,
          rangeStart: "",
          rangeEnd: ""
        }));
        setFieldErrors({
            ...fieldErrors,
            rangeStart: '',
            rangeEnd: ''
          });
    };

    const handleUseRangeChange = (checked) => {
        // A range never creates return or default locations, those options are hidden while it is on
        setLocationInfo(prev => ({
          ...prev,
          useRange: checked,
          rangeStart: checked ? prev.rangeStart : "",
          rangeEnd: checked ? prev.rangeEnd : "",
          returnLocation: checked ? false : prev.returnLocation,
          defaultLocation: checked ? false : prev.defaultLocation
        }));
        setFieldErrors({
            ...fieldErrors,
            rangeStart: '',
            rangeEnd: ''
          });
    };

    const handleReturnLocationChange = (value) => {
        if (value === "true") {
            // Marking a location as return location has consequences, so ask for confirmation first
            setReturnLocationConfirmOpen(true);
        } else {
            handleChange("returnLocation", false);
        }
    };

    const confirmReturnLocation = () => {
        handleChange("returnLocation", true);
    };

    const handleChangeWarehouses = (key,value) => {
        setLocationInfo(prev => ({
          ...prev,
          [key]: value,
          warehouseZoneId:null,
        }));
        setFieldErrors({
            ...fieldErrors,
            [key]: '',
            warehouseZoneId:'',
          });
    }

    const labelWidth = 144;

    return(
        <SHModal
            open={props.open}
            onClose={props.handleClose}
        >
            <Box 
                sx={{
                    textAlign:"center",
                    paddingY:4,
                    paddingX:5,
                    display:"flex",
                    flexDirection:"column",
                    gap:1
                }}>
                <Subheading>{t("new_location")}</Subheading>
                {/* <Text light>The data presented will automatically be filtered through the selected warehouses</Text> */}
            </Box>

            {/* BODY */}
            <Box
                sx={{
                    background:theme.palette.mode === "dark" ? colors.grey[200] : colors.grey[0],
                    padding:3,
                    borderTopLeftRadius:16,
                    borderTopRightRadius:16,
                    display:"flex",
                    flexDirection:"column",
                    gap:2
                }}
            >
                

                <Box 
                    sx={{
                        width:1,
                        display:"flex",
                        alignItems:"center",
                        justifyContent:"space-between",
                        gap:7
                    }}
                >
                    <Box
                        sx={{
                            width:labelWidth,
                            flexShrink:0,
                            display:"flex",
                            alignItems:"center"
                        }}
                    >
                        <Text light>{t("add_range")}</Text>
                        <EMGeneralExplainerBox title={t('add_range')} text={t('em_add_range_text')}/>
                    </Box>

                    <Box
                        sx={{
                            flex:1,
                            display:"flex",
                            height:32
                        }}
                    >
                        <ShSwitch 
                            sx={{
                                px:2,
                                color:colors.txt["primary"],
                                lineHeight:"32px",
                            }}
                            checked={locationInfo.useRange} 
                            onChange={(event) => handleUseRangeChange(event.target.checked)}
                            color="green" />
                    </Box>
                </Box>
                <Box 
                    sx={{
                        width:1,
                        display:"flex",
                        alignItems:"center",
                        justifyContent:"space-between",
                        gap:7
                    }}
                >
                    <Box
                        sx={{
                            width:labelWidth,
                            flexShrink:0,
                        }}
                    >
                        <Text light>{locationInfo.useRange ? t("prefix") : t("location")}</Text>
                    </Box>
                    
                    <Box
                        sx={{
                            flex:1,
                            borderRadius:4,
                            display:"flex",
                        }}
                    >
                        <ShValidatedInput
                            name="location"
                            value={locationInfo.location}
                            onChange={handleChange}
                            error={fieldErrors.location}
                        />
                    </Box>
                </Box>
                {locationInfo.useRange &&
                    <Box 
                        sx={{
                            width:1,
                            display:"flex",
                            alignItems:"center",
                            justifyContent:"space-between",
                            gap:7
                        }}
                    >
                        <Box
                            sx={{
                                width:labelWidth,
                                flexShrink:0,
                            }}
                        >
                            <Text light>{t("range_type")}</Text>
                        </Box>
                        <ToggleButtonGroup
                            color="green"
                            value={locationInfo.rangeType}
                            exclusive
                            onChange={(e,value) => handleRangeTypeChange(value)}
                            aria-label="Range type"
                            sx={{
                                background: colors.grey[100],
                                padding:0.5,
                                borderRadius:5,
                            }}
                        >
                            {["numeric", "alphabetic"].map((rangeType) => (
                                <ToggleButton 
                                    key={rangeType}
                                    sx={{
                                        padding:0,
                                        paddingX:2,
                                        border:0,
                                        lineHeight:"32px",
                                        textTransform:"unset",
                                        borderRadius:"32px !important",
                                        color:colors.txt["primary"],
                                        transition:"250ms",
                                        "&.Mui-selected,&.Mui-selected:hover" : {
                                            background:colors.green[400],
                                            color:`${theme.palette.mode === "dark" ? "rgba(0,0,0,.75)" : "#fff"}`
                                        }
                                    }}
                                    value={rangeType}
                                >
                                    {rangeType === "numeric" ? t('range_numbers') : t('range_letters')}
                                </ToggleButton>
                            ))}
                        </ToggleButtonGroup>
                    </Box>
                }
                {locationInfo.useRange &&
                    <Box 
                        sx={{
                            width:1,
                            display:"flex",
                            alignItems:"flex-start",
                            justifyContent:"space-between",
                            gap:7
                        }}
                    >
                        <Box
                            sx={{
                                width:labelWidth,
                                flexShrink:0,
                                lineHeight:"32px"
                            }}
                        >
                            <Text light>{t("range")}</Text>
                        </Box>
                        
                        <Box
                            sx={{
                                flex:1,
                                display:"flex",
                                gap:2,
                            }}
                        >
                            <ShValidatedInput
                                type={locationInfo.rangeType === "alphabetic" ? "text" : "number"}
                                name="rangeStart"
                                placeholder={locationInfo.rangeType === "alphabetic" ? "A" : t("from")}
                                value={locationInfo.rangeStart}
                                onChange={handleRangeBoundChange}
                                error={fieldErrors.rangeStart}
                            />
                            <ShValidatedInput
                                type={locationInfo.rangeType === "alphabetic" ? "text" : "number"}
                                name="rangeEnd"
                                placeholder={locationInfo.rangeType === "alphabetic" ? "Z" : t("to")}
                                value={locationInfo.rangeEnd}
                                onChange={handleRangeBoundChange}
                                error={fieldErrors.rangeEnd}
                            />
                        </Box>
                    </Box>
                }
                {locationInfo.useRange &&
                    <Box
                        sx={{
                            width:1,
                            paddingX:2,
                            paddingY:1.5,
                            borderRadius:4,
                            background:theme.palette.mode === "dark" ? colors.grey[300] : colors.grey[100],
                        }}
                    >
                        {rangePreview ?
                            <Text>{t("range_preview", rangePreview)}</Text>
                        :
                            <Text light>{t("range_preview_incomplete")}</Text>
                        }
                    </Box>
                }
                <Box 
                    sx={{
                        width:1,
                        display:"flex",
                        alignItems:"center",
                        justifyContent:"space-between",
                        gap:7
                    }}
                >


                    <Box
                        sx={{
                            width:labelWidth,
                            flexShrink:0,
                        }}
                    >
                        <Text light>{t("warehouse")}</Text>
                    </Box>
                    <ShValidatedInputDropdown
                        key={locationInfo.warehouseId}
                        selected={warehouses?.data.findIndex(item => item.id === locationInfo.warehouseId) ?? -1}
                        name="warehouseId"
                        displayName="name"
                        changeField="id"
                        options={warehouses?.data ?? []}
                        noSelection={t("choose_option")} 
                        onChange={handleChangeWarehouses}
                        error={fieldErrors.warehouseId}
                    />
                </Box>
                <Box 
                    sx={{
                        width:1,
                        display:"flex",
                        alignItems:"center",
                        justifyContent:"space-between",
                        gap:7
                    }}
                >

                    <Box
                        sx={{
                            width:labelWidth,
                            flexShrink:0,
                        }}
                    >
                        <Text light>{t("warehouse_zone")}</Text>
                    </Box>
                    
                    {/* <Box
                        sx={{
                            flex:1,
                            backgroundColor : `${theme.palette.mode === "dark" ?colors.grey[300] : colors.grey[100]}`,
                            borderRadius:4,
                            display:"flex",
                            height:32
                        }}
                    > */}
                        <ShValidatedInputDropdown
                            disabled={!locationInfo.warehouseId}
                            key={locationInfo.warehouseZoneId}
                            name="warehouseZoneId"
                            displayName="description"
                            selected={warehouseZones?.data.filter(item => item.warehouse.id === locationInfo.warehouseId).findIndex(item => item.id === locationInfo.warehouseZoneId) ?? -1}
                            changeField="id"
                            // options={warehouseZones?.data ?? []}
                            options={warehouseZones?.data.filter(item => item.warehouse.id === locationInfo.warehouseId) ?? []}
                            noSelection={!locationInfo.warehouseId ? t("select_warehouse_first") : t("choose_option")} 
                            onChange={handleChange}
                            error={fieldErrors.warehouseZoneId}
                        />
                    {/* </Box> */}
                </Box>
                <Box 
                    sx={{
                        width:1,
                        display:"flex",
                        alignItems:"center",
                        justifyContent:"space-between",
                        gap:7
                    }}
                >

                    <Box
                        sx={{
                            width:labelWidth,
                            flexShrink:0,
                        }}
                    >
                        <Text light>{t("location_type")}</Text>
                    </Box>
                    
                    {/* <Box
                        sx={{
                            flex:1,
                            backgroundColor : `${theme.palette.mode === "dark" ?colors.grey[300] : colors.grey[100]}`,
                            borderRadius:4,
                            display:"flex",
                            height:32
                        }}
                    > */}
                        <ShValidatedInputDropdown
                            name="warehouseLocationTypeId"
                            displayName="description"
                            changeField="id"
                            options={locationTypes?.data ?? []}
                            noSelection={t("choose_option")} 
                            onChange={handleChange}
                            error={fieldErrors.warehouseLocationTypeId}
                        />
                    {/* </Box> */}
                </Box>
                <Box 
                    sx={{
                        width:1,
                        display:"flex",
                        alignItems:"center",
                        justifyContent:"space-between",
                        gap:7
                    }}
                >
                    <Box
                        sx={{
                            width:labelWidth,
                            flexShrink:0,
                        }}
                    >
                        <Text light>{t("reach_type")}</Text>
                    </Box>
                    <ShValidatedInputDropdown
                        name="reachTypeId"
                        displayName="description"
                        changeField="id"
                        options={reachTypes?.data ?? []}
                        noSelection={t("choose_option")} 
                        onChange={handleChange}
                        error={fieldErrors.reachTypeId}
                    />
                </Box>
                <Box 
                    sx={{
                        width:1,
                        display:"flex",
                        alignItems:"center",
                        justifyContent:"space-between",
                        gap:7
                    }}
                >
                    <Box
                        sx={{
                            width:labelWidth,
                            flexShrink:0,
                            display: "flex",
                            alignItems: "center"
                        }}
                    >
                        <Text light>{t("packstation_zone_range")}</Text>
                        <EMGeneralExplainerBox title={t('packstation_zone_range')} text={t('em_packstation_zone_range_text')}/>
                    </Box>
                    <ShValidatedInputDropdown
                        key={locationInfo.packstationZoneRange}
                        name="packstationZoneRange"
                        displayName="name"
                        changeField="id"
                        options={packstationZoneRangeOptions(t)}
                        selected={packstationZoneRangeOptions(t).findIndex(item => item.id === (locationInfo.packstationZoneRange || null))}
                        noSelection={t("choose_option")} 
                        onChange={handleChange}
                        error={fieldErrors.packstationZoneRange}
                    />
                </Box>
                {!locationInfo.useRange && <>
                    <Box
                        sx={{
                            width:1,
                            display:"flex",
                            alignItems:"center",
                            justifyContent:"space-between",
                            gap:7
                        }}
                    >
                        <Box
                            sx={{
                                width:labelWidth,
                                flexShrink:0,
                                display: "flex",
                                alignItems: "center"
                            }}
                        >
                            <Text light>{t("return_location")}</Text>
                            <EMGeneralExplainerBox title={t('return_location')} text={t('em_return_location_text')}/>
                        </Box>
                        <ToggleButtonGroup
                            color={locationInfo.returnLocation ? "green" : "red"}
                            value={locationInfo.returnLocation ? "true" : "false"}
                            exclusive
                            onChange={(e,value) => handleReturnLocationChange(e.target.value)}
                            aria-label="Active"
                            className={cx(`${locationInfo.returnLocation}`, {
                                'active': locationInfo.returnLocation
                            })}
                            sx={{
                                background: colors.red[100],
                                padding:0.5,
                                borderRadius:5,
                                transition:"250ms",
                                "&.active" : {
                                    background:colors.green[100],
                                    transition:"250ms",
                                },
                            }}
                        >
                            <ToggleButton 
                                sx={{
                                    padding:0,
                                    paddingX:2,
                                    border:0,
                                    lineHeight:"32px",
                                    textTransform:"unset",
                                    borderRadius:"32px !important",
                                    color:colors.green[400],
                                    transition:"250ms",
                                    "&.Mui-selected,&.Mui-selected:hover" : {
                                        background:colors.red[400],
                                        color:`${theme.palette.mode === "dark" ? "rgba(0,0,0,.75)" : "#fff"}`
                                    }
                                }}
                                value="false">
                                {t('no')}
                            </ToggleButton>
                            <ToggleButton 
                                sx={{
                                    padding:0,
                                    paddingX:2,
                                    border:0,
                                    lineHeight:"32px",
                                    textTransform:"unset",
                                    borderRadius:"32px !important",
                                    color:colors.red[400],
                                    transition:"250ms",
                                    "&.Mui-selected,&.Mui-selected:hover" : {
                                        background:colors.green[400],
                                        color:`${theme.palette.mode === "dark" ? "rgba(0,0,0,.75)" : "#fff"}`
                                    }
                                }}
                                value="true"
                            >
                                {t('yes')}
                            </ToggleButton>
                        </ToggleButtonGroup>
                    </Box>
                    <Box
                        sx={{
                            width:1,
                            display:"flex",
                            alignItems:"center",
                            justifyContent:"space-between",
                            gap:7
                        }}
                    >
                        <Box
                            sx={{
                                width:140,
                                flexShrink:0,
                                display: "flex",
                                alignItems: "center"
                            }}
                        >
                            <Text light>{t("default_location")}</Text>
                            <EMGeneralExplainerBox title={t('default_location')} text={t('em_default_location_text')}/>
                        </Box>
                        <ToggleButtonGroup
                            color={locationInfo.defaultLocation ? "green" : "red"}
                            value={locationInfo.defaultLocation ? "true" : "false"}
                            exclusive
                            onChange={(e,value) => handleChange("defaultLocation", e.target.value === "true" ? true : false)}
                            aria-label="Active"
                            className={cx(`${locationInfo.defaultLocation}`, {
                                'active': locationInfo.defaultLocation
                            })}
                            sx={{
                                background: colors.red[100],
                                padding:0.5,
                                borderRadius:5,
                                transition:"250ms",
                                "&.active" : {
                                    background:colors.green[100],
                                    transition:"250ms",
                                },
                            }}
                        >
                            <ToggleButton 
                                sx={{
                                    padding:0,
                                    paddingX:2,
                                    border:0,
                                    lineHeight:"32px",
                                    textTransform:"unset",
                                    borderRadius:"32px !important",
                                    color:colors.green[400],
                                    transition:"250ms",
                                    "&.Mui-selected,&.Mui-selected:hover" : {
                                        background:colors.red[400],
                                        color:`${theme.palette.mode === "dark" ? "rgba(0,0,0,.75)" : "#fff"}`
                                    }
                                }}
                                value="false">
                                {t('no')}
                            </ToggleButton>
                            <ToggleButton 
                                sx={{
                                    padding:0,
                                    paddingX:2,
                                    border:0,
                                    lineHeight:"32px",
                                    textTransform:"unset",
                                    borderRadius:"32px !important",
                                    color:colors.red[400],
                                    transition:"250ms",
                                    "&.Mui-selected,&.Mui-selected:hover" : {
                                        background:colors.green[400],
                                        color:`${theme.palette.mode === "dark" ? "rgba(0,0,0,.75)" : "#fff"}`
                                    }
                                }}
                                value="true"
                            >
                                {t('yes')}
                            </ToggleButton>
                        </ToggleButtonGroup>
                    </Box>
                </>}
            </Box>
            <Box
                component="footer"
                sx={{
                    
                    display:"block",
                    width:1,
                    paddingBottom: isStandAlone ? "32px" : "16px",
                    display:"flex",
                    alignItems:"center",
                    paddingRight:3,
                    zIndex:1058,
                    position:"relative",
                    paddingTop:2,
                    background:`${theme.palette.mode === "dark" ? colors.grey[200] : colors.grey[0]}`,
                    borderBottomLeftRadius:24,
                    borderBottomRightRadius:24,
                    justifyContent:"space-between",
                    "@media screen and (max-width: 47.9375em)" : {
                        paddingRight:0,
                    }
                }}
            >
                <Shbutton isCancel={true} className={"always-visible"} onClick={props.handleClose} color="primary" variant="text" startIcon={<ArrowRightAltIcon/>}>{t("cancel")}</Shbutton>
                <ButtonGroup version={2}>
                    <Shbutton isLoading={isLoading} className={"group-btn"} color={"green"}
                    variant="contained" 
                    onClick={() => SaveWarehouseLocation(locationInfo, props)}
                    >{t("add")}</Shbutton>
                </ButtonGroup>
            </Box>
            <MConfirmation
                open={returnLocationConfirmOpen}
                handleClose={() => setReturnLocationConfirmOpen(false)}
                confirm={confirmReturnLocation}
                title={t("return_location_confirm_title")}
                content={t("return_location_confirm_text")}
            />
        </SHModal>
    )
}

export default MAddWarehouseLocation;
